import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CAlert
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import DataTableComponent from "components/DataTableComponent";
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";
import AlertNotification, { triggerAlert } from 'components/globals/AlertNotification';
import ToastNotification from 'components/globals/ToastNotification';

const EventList = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const location = useLocation();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default to current year
    const [availableYears, setAvailableYears] = useState([]);
    const [users, setUsers] = useState([]);
    const [toast, setToast] = useState(null);
    const [previewEvent, setPreviewEvent] = useState(null);
    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    useEffect(() => {
        const fetchEvents = async (year = selectedYear) => {
            try {
                const response = await apiClient.get(`/events/?year=${year}`);
                setEvents(response.data.events);
                setAvailableYears(response.data.available_years);
                setPreviewEvent((current) => current
                    ? response.data.events.find((event) => event.id === current.id) || null
                    : current);
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        const fetchUsers = async () => {
            try {
                let response = await apiClient.get('/users/');
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        fetchEvents(selectedYear);
        fetchUsers();
    }, [location.pathname, selectedYear]);

    const handleDelete = async (eventId) => {
        try {
            await apiClient.delete(`/events/${eventId}/`);
            setEvents(events.filter((event) => event.id !== eventId));
            setPreviewEvent((current) => (current && current.id === eventId ? null : current));
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    const setNotRealized = async (eventId) => {
        try {
            await apiClient.post(`/events/${eventId}/set-not-realized/`, {});
            triggerAlert('Formação/Evento definida como não realizada com sucesso!', 'success');
            setEvents((events) =>
                events.map((event) =>
                    event.id === eventId ? { ...event, effectiveness: "Não avaliada" } : event
                )
            );
        } catch (error) {
            console.error('Error setting event as not realized:', error);
        }
    }

    const evaluationReminder = async () => {
        try {
            await apiClient.post(`/events/evaluation-reminder/`, {});
            setToast({ message: 'Lembretes de avalição de Formação/Evento enviados com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Error sending evaluation reminder:', error);
            setToast({ message: 'Erro ao enviar lembrete de avaliação.', type: 'error' });
            return;
        }
    }

    const handleDownloadExcel = async () => {
        try {
            const response = await apiClient.get(
                `/events/?year=${selectedYear}&export=excel`,
                {
                    responseType: "blob",
                },
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `formacoes_eventos_${selectedYear}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting events excel:', error);
            setToast({ message: 'Erro ao exportar Excel das Formações/Eventos.', type: 'error' });
        }
    };

    const generateCertificate = async (eventId) => {
        try {
            const response = await apiClient.get(`/events/${eventId}/certificate/${user_id}/`, { 
                responseType: "blob", 
                headers: { 
                    "Accept": "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                } 
            });
    
            const blob = new Blob([response.data], { type: response.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
    
            const a = document.createElement("a");
            a.href = url;
            a.download = `MOD-105.2, Certificado de Ação de Formação.pptx`;
            document.body.appendChild(a);
            a.click();
    
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error generating certificate:", error);
        }
    };

    const openNotificationModal = async (designation, eventId) => { }

    const getEffectivenessLabel = (effectiveness) => effectiveness || "Por avaliar";

    const getParticipantsDisplay = (row, onClick) =>
        row.participants.length > 0 ? (
            <ul
                className="mb-0 ps-3"
                style={onClick ? { cursor: "pointer" } : undefined}
                onClick={onClick}
            >
                {row.participants.map(userID => {
                    const user = users.find(u => u.id === userID);
                    if (user){
                        return (
                            <li key={userID}>
                                {user ? `${user.first_name} ${user.last_name}` : "Unknown"}
                            </li>
                        );
                    }
                }
            )}
            </ul>
        ) : (
            <span className="text-muted">Sem dados</span>
        );

    const columns = [
        { name: "Tipo", selector: (row) => row.event_type, sortable: true, className: 'text-center' },
        { name: "Designação", selector: (row) => row.designation, sortable: true, className: 'text-center' },
        {
            name: "Destinatários",
            cell: (row) => getParticipantsDisplay(row, () => setPreviewEvent(row)),
            sortable: false,
        },
        { name: "Data de Aprovação", selector: (row) => row.approved_date, sortable: true },
        { name: "Data da Formação", selector: (row) => row.start_date, sortable: true },
        { name: "Eficácia", selector: (row) => getEffectivenessLabel(row.effectiveness), sortable: true },
        {
            name: "Eficácia Chefia",
            selector: (row) => row.effectiveness === "Não realizada" ? "Não realizada" : (row.management_effectiveness_label || "Por avaliar"),
            sortable: true,
        },
    ];

    return (
        <>
            <AlertNotification />
            <ToastNotification message={toast?.message} type={toast?.type} visible={!!toast} />
            <CCard>
                
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Formações/Eventos</h3>
                    <div className="d-flex gap-2">
                        <CButton color="success" onClick={handleDownloadExcel}>
                            Exportar Excel
                        </CButton>
                        <Link to="/formacoes-eventos/new">
                            <CButton color="info" className="text-white">Nova Formação/Evento</CButton>
                        </Link>
                    </div>
                </CCardHeader>
                <CCardBody>
                    {is_director && (
                        <div className="d-flex justify-content-between mb-3 text-end">
                            <CButton color="info" className="text-white" onClick={evaluationReminder}>Lembrar Avaliação</CButton>
                        </div>
                    )}
                    <DataTableComponent
                        columns={columns}
                        data={events}
                        years={availableYears}  // Optional
                        selectedYear={selectedYear}  // Optional
                        onYearChange={(newYear) => setSelectedYear(newYear)}  // Optional
                        onRowClicked={setPreviewEvent}
                    />
                </CCardBody>
            </CCard>
            <RecordDetailOffcanvas
                record={previewEvent}
                onHide={() => setPreviewEvent(null)}
                title="Formação/Evento"
                subtitle={previewEvent?.designation}
                fields={previewEvent ? [
                    { label: "Tipo", value: previewEvent.event_type },
                    { label: "Designação", value: previewEvent.designation },
                    { label: "Destinatários", value: getParticipantsDisplay(previewEvent), plain: false },
                    { label: "Data de Aprovação", value: previewEvent.approved_date },
                    { label: "Data da Formação", value: previewEvent.start_date },
                    { label: "Eficácia", value: getEffectivenessLabel(previewEvent.effectiveness) },
                    {
                        label: "Eficácia Chefia",
                        value: previewEvent.effectiveness === "Não realizada"
                            ? "Não realizada"
                            : (previewEvent.management_effectiveness_label || "Por avaliar"),
                    },
                ] : []}
                headerActions={previewEvent && <>
                    {(is_director || (previewEvent.approved && (!previewEvent.effectiveness || (previewEvent.participants.includes(user_id) && !["Não realizada", "Não avaliada"].includes(previewEvent.effectiveness))))) && (
                        <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/formacoes-eventos/${previewEvent.id}/avaliacao`)}>
                            Avaliação
                        </CButton>
                    )}
                    {previewEvent.participants.some((p) => p === user_id) && (
                        <CButton color="info" variant="outline" size="sm" onClick={() => generateCertificate(previewEvent.id)}>
                            Certificado
                        </CButton>
                    )}
                    <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/formacoes-eventos/${previewEvent.id}/relatorio`)}>
                        Relatório
                    </CButton>
                    {(previewEvent.participants.some((p) => p.id === user_id) || is_director) && (
                        <CButton color="info" variant="outline" size="sm" onClick={() => openNotificationModal(previewEvent.designation, previewEvent.id)}>
                            Notificação Relatório
                        </CButton>
                    )}
                    <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/formacoes-eventos/${previewEvent.id}`)}>
                        {previewEvent.approved ? "Ver" : "Editar"}
                    </CButton>
                    {previewEvent.approved && (!previewEvent.effectiveness || previewEvent.effectiveness === "") && (is_director || previewEvent.participants.includes(user_id)) && (
                        <CButton color="secondary" size="sm" onClick={() => setNotRealized(previewEvent.id)}>
                            Definir como 'Não Realizada'
                        </CButton>
                    )}
                    {is_director && !previewEvent.effectiveness && (
                        <CButton color="danger" size="sm" className="text-white" onClick={() => {
                            if (window.confirm("Tem a certeza que quer eliminar esta formação/evento?")) handleDelete(previewEvent.id);
                        }}>
                            Eliminar
                        </CButton>
                    )}
                </>}
            />
        </>
    );
};

export default EventList;
